import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = document.querySelector('.v4-prototype-shell');

if (root && root.dataset.v4PrototypeReady !== 'true') {
  root.dataset.v4PrototypeReady = 'true';
  const menu = root.querySelector('[data-v4p-mobile-menu]');
  const backdrop = root.querySelector('[data-v4p-backdrop]');
  const toast = root.querySelector('[data-v4p-toast]');
  const filterSheet = root.querySelector('[data-v4p-filter-sheet]');
  const mainProductImage = root.querySelector('#V4PdpMainImage');
  const productLightbox = root.querySelector('[data-v4p-lightbox]');
  const productLightboxImage = root.querySelector('[data-v4p-lightbox-image]');
  const productZoomButton = root.querySelector('[data-v4p-zoom-open]');

  const updateMainProductImage = ({ src, srcset, alt }) => {
    if (!mainProductImage || !src) return;
    if (srcset) mainProductImage.srcset = srcset;
    else mainProductImage.removeAttribute('srcset');
    mainProductImage.src = src;
    if (alt) mainProductImage.alt = alt;
  };

  const setActiveGalleryButton = (activeButton) => {
    if (!activeButton) return;
    root.querySelectorAll('[data-v4p-gallery-image]').forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    const count = root.querySelector('.pdp-gallery__count');
    if (count) count.textContent = `${activeButton.dataset.v4pGalleryIndex} / ${root.querySelectorAll('[data-v4p-gallery-image]').length}`;
  };

  const openProductLightbox = () => {
    if (!productLightbox || !productLightboxImage || !mainProductImage) return;
    productLightboxImage.src = mainProductImage.src;
    productLightboxImage.srcset = mainProductImage.srcset;
    productLightboxImage.sizes = '100vw';
    productLightboxImage.alt = mainProductImage.alt;
    if (typeof productLightbox.showModal === 'function') productLightbox.showModal();
    else productLightbox.setAttribute('open', '');
  };

  const closeProductLightbox = () => {
    if (!productLightbox) return;
    if (typeof productLightbox.close === 'function' && productLightbox.open) productLightbox.close();
    else productLightbox.removeAttribute('open');
  };

  const closeMenu = () => {
    menu?.classList.remove('is-open');
    menu?.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
  };

  const openMenu = () => {
    menu?.classList.add('is-open');
    menu?.setAttribute('aria-hidden', 'false');
    backdrop?.classList.add('is-visible');
    document.body.classList.add('is-locked');
  };

  const closeFilters = () => {
    filterSheet?.classList.remove('is-open');
    filterSheet?.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
  };

  const openFilters = () => {
    filterSheet?.classList.add('is-open');
    filterSheet?.setAttribute('aria-hidden', 'false');
    backdrop?.classList.add('is-visible');
    document.body.classList.add('is-locked');
  };

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
  };

  const openThemeCart = () => {
    const trigger = document.querySelector('#header-group [ref="cartButton"], #header-group button[aria-label="Cart"], #header-group button[aria-label*="cart" i]');
    trigger?.click();
  };

  const addProduct = async (form) => {
    const formData = new FormData(form);
    const id = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;
    const submit = form.querySelector('button[type="submit"]');
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((component) => component.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    root.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(id), quantity }], promise: deferred.promise }));
    submit?.setAttribute('aria-busy', 'true');
    if (submit) submit.disabled = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add item');
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'v4-prototype-home', sourceId: form.id || 'v4-prototype-card', itemCount: quantity, sections: result.sections, didError: false } });
      root.querySelectorAll('.cart-count').forEach((count) => { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; });
      showToast('Added to cart');
    } catch (error) {
      deferred.reject(error);
      root.dispatchEvent(new CartErrorEvent({ error: error instanceof Error ? error.message : 'Unable to add item', code: 'INVALID' }));
      showToast('Unable to add item');
    } finally {
      submit?.removeAttribute('aria-busy');
      if (submit) submit.disabled = false;
    }
  };

  root.addEventListener('click', (event) => {
    const target = event.target.closest('button, a');
    if (!target) return;
    if (target.matches('[data-v4p-open-menu]')) openMenu();
    if (target.matches('[data-v4p-close-menu]') || target.closest('[data-v4p-mobile-menu] a')) closeMenu();
    if (target.matches('[data-v4p-open-filters]')) openFilters();
    if (target.matches('[data-v4p-close-filters]')) closeFilters();
    if (target.matches('[data-v4p-open-cart]')) openThemeCart();
    if (target.matches('[data-v4p-zoom-open]')) openProductLightbox();
    if (target.matches('[data-v4p-zoom-close]')) closeProductLightbox();

    const galleryButton = target.closest('[data-v4p-gallery-image]');
    if (galleryButton) {
      updateMainProductImage({
        src: galleryButton.dataset.v4pGalleryImage,
        srcset: galleryButton.dataset.v4pGallerySrcset,
        alt: galleryButton.querySelector('img')?.alt,
      });
      setActiveGalleryButton(galleryButton);
    }

    const quantityInput = root.querySelector('[data-v4p-quantity-input]');
    const quantityText = root.querySelector('[data-v4p-quantity]');
    if (target.matches('[data-v4p-quantity-minus], [data-v4p-quantity-plus]') && quantityInput) {
      const delta = target.matches('[data-v4p-quantity-plus]') ? 1 : -1;
      const next = Math.max(1, Number(quantityInput.value || 1) + delta);
      quantityInput.value = String(next);
      if (quantityText) quantityText.textContent = String(next);
    }

    const variantButton = target.closest('[data-v4p-variant-id]');
    if (variantButton) {
      const variantInput = root.querySelector('[data-v4p-variant-input]');
      if (variantInput) variantInput.value = variantButton.dataset.v4pVariantId;
      root.querySelectorAll('[data-v4p-variant-id]').forEach((button) => {
        const isActive = button === variantButton;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
      root.querySelectorAll('[data-v4p-selected-variant]').forEach((label) => { label.textContent = variantButton.dataset.v4pVariantTitle; });
      root.querySelectorAll('[data-v4p-pdp-price], [data-v4p-button-price]').forEach((price) => { price.textContent = variantButton.dataset.v4pVariantPrice; });
      const variantImage = variantButton.dataset.v4pVariantImage;
      updateMainProductImage({
        src: variantImage,
        srcset: variantButton.dataset.v4pVariantSrcset,
        alt: variantButton.dataset.v4pVariantImageAlt,
      });
      const matchingGalleryButton = root.querySelector(`[data-v4p-gallery-image][data-v4p-media-id="${variantButton.dataset.v4pMediaId}"]`);
      if (matchingGalleryButton) setActiveGalleryButton(matchingGalleryButton);
    }

    if (target.matches('[data-v4p-sticky-submit]')) root.querySelector('.v4p-pdp-form')?.requestSubmit();
  });

  root.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.v4p-product-form')) return;
    event.preventDefault();
    addProduct(form);
  });

  root.addEventListener('change', (event) => {
    const select = event.target.closest('[data-v4p-sort]');
    if (select) select.form?.submit();
  });

  backdrop?.addEventListener('click', () => { closeMenu(); closeFilters(); });
  productLightbox?.addEventListener('click', (event) => {
    if (event.target === productLightbox || event.target.matches('.pdp-lightbox__surface')) closeProductLightbox();
  });
  productLightbox?.addEventListener('close', () => window.setTimeout(() => productZoomButton?.focus(), 0));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { closeMenu(); closeFilters(); } });
}
